import type {EntityType} from 'src/types/ats-entities';
import type {DynamicLabelExtra} from 'src/action-creators/dynamic-labels';
import type {SuggestionProps} from 'src/components/lib/token-list-input/new-stuff';

import * as React from 'react';
import {useSelector} from 'react-redux';

import {
  entityColorMapping,
  getAttributeRelationshipPath,
  generateAttributeGrammarTokens,
} from 'src/utils/entities';
import {classify} from 'src/utils';

import {selectEntityTypeMappingsByName} from 'src/selectors/ats-entities';

import {AutoTruncatedText} from 'src/components/lib/truncated-text/truncated-text';

import common from 'src/styles/common.css';
import css from './variable-picker.css';


export const VariablePickerDropdownRow = ({
  option: dynamicLabel,
  // @ts-expect-error - TS2739 - Type '{}' is missing the following properties from type '{ entityType: EntityType; isHighlighted: boolean; }': entityType, isHighlighted
  extras = {},
  searchString,
  onClick,
  onMouseDown,
  selected,
  isSelected,
}: {
  option: DynamicLabelExtra;
  searchString: string;
  preventSelection: () => any;
  extras: {
    entityType: EntityType;
    isHighlighted: boolean;
  };
  onClick?: (evt: React.MouseEvent) => unknown;
  onMouseDown?: (evt: React.KeyboardEvent) => unknown;
  selected?: boolean;
  isSelected: boolean;
}): React.ReactElement<React.ComponentProps<'div'>> => {
  let children;

  const entitiesByName = useSelector(selectEntityTypeMappingsByName);

  if (!dynamicLabel.entity_type) {
    if (searchString && !selected) {
      children = `Create ${searchString}`;
    } else {
      children = (
        <div className={css.variableName}>{`${dynamicLabel.value}`}</div>
      );
    }
  } else {
    const color =
      (dynamicLabel.display_entity_type &&
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly internal_employee: { readonly name: "purple"; readonly text: "colorPurpleShadow"; readonly background: "colorPurpleTint"; }; readonly ab_web_applicant: { readonly name: "purple"; readonly text: "colorPurpleShadow"; readonly background: "colorPurpleTint"; }; ... 24 more ...; readonly job_match_variable: { ...'.
        common[entityColorMapping[dynamicLabel?.sense_name]?.text]) ||
      common.colorGray6;

    const entityRelationshipPath =
      dynamicLabel.entityRelationshipPath ??
      getAttributeRelationshipPath(
        dynamicLabel.label,
        dynamicLabel.entityType,
        entitiesByName,
      );

    const tokens = generateAttributeGrammarTokens(entityRelationshipPath);
    children = (
      <>
        <div
          key={dynamicLabel.name}
          style={{
            color,
          }}
          className={css.variableName}
        >
          {`${entityRelationshipPath[0]}`}
        </div>

        <section className={css.dropdownPipeSeparator} role="presentation">
          |
        </section>
        {tokens.map((token, index) => (
          <div
            key={index} // eslint-disable-line react/no-array-index-key
            className={classify(css.dropdownPhraseElement, {
              [css.dropdownBoldPhraseElement]: index % 2 !== 0,
            })}
          >
            {token}
          </div>
        ))}
      </>
    );
  }

  return (
    <div
      className={classify(
        css.dropdownResult,
        {[css.dropdownResultHighlighted]: isSelected},
        {[css.dropdownResultSelected]: selected},
      )}
      onClick={onClick}
      // @ts-expect-error - TS2322 - Type '((evt: KeyboardEvent<Element>) => unknown) | undefined' is not assignable to type 'MouseEventHandler<HTMLDivElement> | undefined'.
      onMouseDown={onMouseDown}
      data-qa-id="variable-picker-dropdown-row"
    >
      {children}
    </div>
  );
};

export function NewVariableDropdownRow({
  className,
  option: dynamicLabel,
  onClick,
  isSelected,
}: SuggestionProps<DynamicLabelExtra>): React.ReactElement<
  React.ComponentProps<'div'>
> {
  const entitiesByName = useSelector(selectEntityTypeMappingsByName);

  let children;

  if (!dynamicLabel.entity_type) {
    if (dynamicLabel.arbitrary) {
      children = `Create ${dynamicLabel.value}`;
    } else {
      children = (
        <div className={css.variableName}>{`${dynamicLabel.value}`}</div>
      );
    }
  } else {
    const color =
      (dynamicLabel.display_entity_type &&
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly internal_employee: { readonly name: "purple"; readonly text: "colorPurpleShadow"; readonly background: "colorPurpleTint"; }; readonly ab_web_applicant: { readonly name: "purple"; readonly text: "colorPurpleShadow"; readonly background: "colorPurpleTint"; }; ... 24 more ...; readonly job_match_variable: { ...'.
        common[entityColorMapping[dynamicLabel?.sense_name]?.text]) ||
      common.colorGray6;

    const entityRelationshipPath =
      dynamicLabel.entityRelationshipPath ??
      getAttributeRelationshipPath(
        dynamicLabel.label,
        dynamicLabel.entity_type,
        entitiesByName,
      );

    const tokens = generateAttributeGrammarTokens(entityRelationshipPath);
    children = (
      <>
        <div
          key={dynamicLabel.label}
          style={{
            color,
          }}
          className={css.variableName}
        >
          {`${entityRelationshipPath[0]}`}
        </div>

        <section className={css.dropdownPipeSeparator} role="presentation">
          |
        </section>
        {tokens.map((token, index) => (
          <div
            key={index} // eslint-disable-line react/no-array-index-key
            className={classify(css.dropdownPhraseElement, {
              [css.dropdownBoldPhraseElement]: index % 2 !== 0,
            })}
          >
            {token}
          </div>
        ))}
      </>
    );
  }

  return (
    <div
      className={classify(css.dropdownResult, {
        [css.dropdownResultHighlighted]: isSelected,
      })}
      onClick={onClick}
      data-qa-id="variable-picker-dropdown-row"
    >
      {children}
    </div>
  );
}
